import type { Education } from '@applicator/shared'
import type { SupabaseClient } from '@supabase/supabase-js'

import { RESUME_SECTIONS } from '../components/ResumeDetail/constants'

const EDUCATION_DISPLAY_ORDER = RESUME_SECTIONS.findIndex(
  (section) => section.type === 'education',
)

type EducationInclusion = Pick<
  Education,
  | 'included'
  | 'locationIncluded'
  | 'degreeIncluded'
  | 'startDateIncluded'
  | 'endDateIncluded'
  | 'additionalInformationIncluded'
>

const EDUCATION_INCLUSION_OFF: EducationInclusion = {
  included: false,
  locationIncluded: false,
  degreeIncluded: false,
  startDateIncluded: false,
  endDateIncluded: false,
  additionalInformationIncluded: false,
}

export function educationHasContent(educations: Education[]): boolean {
  if (!Array.isArray(educations) || educations.length === 0) return false

  return educations.some((education) => {
    const hasInstitution = String(education.institution ?? '').trim().length > 0
    const hasDegree = String(education.degree ?? '').trim().length > 0
    const hasField = String(education.fieldOfStudy ?? '').trim().length > 0
    const hasLocation = String(education.location ?? '').trim().length > 0
    const hasDates =
      String(education.startDate ?? '').trim().length > 0 ||
      String(education.endDate ?? '').trim().length > 0
    const hasInfo = String(education.additionalInformation ?? '').trim().length > 0
    return hasInstitution || hasDegree || hasField || hasLocation || hasDates || hasInfo
  })
}

function parseEducationContent(raw: unknown): Education[] | null {
  if (!Array.isArray(raw)) return null
  const cloned = cloneEducationList(raw as Education[])
  return educationHasContent(cloned) ? cloned : null
}

export function cloneEducationList(educations: Education[]): Education[] {
  return educations.map((education) => ({ ...education }))
}

function extractEducationInclusion(education: Education): EducationInclusion {
  return {
    included: education.included ?? false,
    locationIncluded: education.locationIncluded ?? false,
    degreeIncluded: education.degreeIncluded ?? false,
    startDateIncluded: education.startDateIncluded ?? false,
    endDateIncluded: education.endDateIncluded ?? false,
    additionalInformationIncluded: education.additionalInformationIncluded ?? false,
  }
}

function defaultEducationInclusion(allOn: boolean): EducationInclusion {
  if (!allOn) return { ...EDUCATION_INCLUSION_OFF }
  return {
    included: true,
    locationIncluded: true,
    degreeIncluded: true,
    startDateIncluded: true,
    endDateIncluded: true,
    additionalInformationIncluded: true,
  }
}

/** Catalog fingerprint: entry ids and text fields only (order-sensitive). */
export function normalizeEducationCatalogFingerprint(educations: Education[]): string {
  return JSON.stringify(
    educations.map((education) => ({
      id: education.id,
      institution: education.institution ?? '',
      location: education.location ?? '',
      degree: education.degree ?? '',
      fieldOfStudy: education.fieldOfStudy ?? '',
      startDate: education.startDate ?? '',
      endDate: education.endDate ?? '',
      additionalInformation: education.additionalInformation ?? '',
    })),
  )
}

export function isEducationCatalogEqual(a: Education[], b: Education[]): boolean {
  return normalizeEducationCatalogFingerprint(a) === normalizeEducationCatalogFingerprint(b)
}

/** Union account + resume catalogs so resume-only entries survive reload before account sync. */
export function unionEducationCatalogs(
  accountCatalog: Education[],
  resumeCatalog: Education[],
): Education[] {
  const byId = new Map<string, Education>()

  for (const education of accountCatalog) {
    byId.set(education.id, { ...education })
  }

  for (const education of resumeCatalog) {
    const existing = byId.get(education.id)
    if (!existing) {
      byId.set(education.id, { ...education })
      continue
    }
    byId.set(education.id, {
      ...existing,
      ...education,
      id: education.id,
    })
  }

  return [...byId.values()]
}

export function mergeEducationCatalogWithResumeInclusions(
  catalog: Education[],
  resumeEducation: Education[] | null | undefined,
  options: { defaultIncluded?: boolean } = {},
): Education[] {
  const inclusionById = new Map<string, EducationInclusion>()
  for (const education of resumeEducation ?? []) {
    if (education.id) {
      inclusionById.set(education.id, extractEducationInclusion(education))
    }
  }

  const fallback = defaultEducationInclusion(options.defaultIncluded ?? false)

  return catalog.map((education) => ({
    ...education,
    ...(inclusionById.get(education.id) ?? fallback),
  }))
}

/** Most recently updated resume education catalog for the account (optional exclude). */
export async function fetchAccountEducation(
  supabase: SupabaseClient,
  options: { excludeResumeId?: string } = {},
): Promise<Education[] | null> {
  const { excludeResumeId } = options

  const {
    data: { user },
    error: authError,
  } = await supabase.auth.getUser()
  if (authError) throw authError
  if (!user) return null

  const { data: resumes, error: resumesError } = await supabase
    .from('resumes')
    .select('id')
    .eq('user_id', user.id)

  if (resumesError) throw resumesError

  const resumeIds = (resumes ?? [])
    .map((resume) => resume.id)
    .filter((resumeId) => resumeId !== excludeResumeId)
  if (resumeIds.length === 0) return null

  const { data: contentRows, error: contentError } = await supabase
    .from('resume_content')
    .select('resume_id, content, updated_at')
    .in('resume_id', resumeIds)
    .eq('section_type', 'education')
    .order('updated_at', { ascending: false })

  if (contentError) throw contentError

  for (const row of contentRows ?? []) {
    const parsed = parseEducationContent(row.content)
    if (parsed) {
      return cloneEducationList(parsed)
    }
  }

  return null
}

/** Propagate education catalog to every resume; preserve per-resume inclusion flags. */
export async function syncAccountEducationCatalogToAllResumes(
  supabase: SupabaseClient,
  catalog: Education[],
  options: {
    sourceResumeId?: string
    sourceResumeEducation?: Education[]
  } = {},
): Promise<void> {
  const { sourceResumeId, sourceResumeEducation } = options

  const {
    data: { user },
    error: authError,
  } = await supabase.auth.getUser()
  if (authError) throw authError
  if (!user) return

  const { data: resumes, error: resumesError } = await supabase
    .from('resumes')
    .select('id')
    .eq('user_id', user.id)

  if (resumesError) throw resumesError

  const resumeIds = (resumes ?? []).map((resume) => resume.id)
  if (resumeIds.length === 0) return

  const { data: contentRows, error: contentError } = await supabase
    .from('resume_content')
    .select('resume_id, content')
    .in('resume_id', resumeIds)
    .eq('section_type', 'education')

  if (contentError) throw contentError

  const educationByResume = new Map<string, Education[]>()
  for (const row of contentRows ?? []) {
    const parsed = parseEducationContent(row.content)
    if (parsed) {
      educationByResume.set(row.resume_id, parsed)
    }
  }

  const catalogClone = cloneEducationList(catalog)
  const rows = resumeIds.map((resumeId) => {
    let content: Education[]
    if (sourceResumeId && resumeId === sourceResumeId && sourceResumeEducation) {
      content = cloneEducationList(sourceResumeEducation)
    } else {
      content = mergeEducationCatalogWithResumeInclusions(
        catalogClone,
        educationByResume.get(resumeId) ?? [],
      )
    }

    return {
      resume_id: resumeId,
      section_type: 'education' as const,
      content,
      display_order: EDUCATION_DISPLAY_ORDER,
    }
  })

  const { error: upsertError } = await supabase.from('resume_content').upsert(rows, {
    onConflict: 'resume_id,section_type',
    ignoreDuplicates: false,
  })

  if (upsertError) throw upsertError
}
